import { useLanguageContext } from "@/contexts/LanguageContext";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useUpdateLanguage } from "@/hooks/queries/lazo-user/useLazoUserQueries";
import { Avatar, Box, CircularProgress, MenuItem, Typography } from "@mui/material";
import { useState } from "react";
import { DropdownMenu } from "./DropdownMenu";

const languages = {
  en: {
    label: "English",
    flag: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/88/United-states_flag_icon_round.svg/250px-United-states_flag_icon_round.svg.png",
  },
  es: {
    label: "Español",
    flag: "https://cdn-icons-png.flaticon.com/512/6195/6195567.png",
  },
};

export function LanguageSwitch() {
  const { language, isLoading, setLanguage } = useLanguageContext();
  const loggedUser = AuthService.getUser();
  const updateLanguageMutation = useUpdateLanguage(loggedUser?.externalId || "");
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageSelect = (newLanguage: "en" | "es") => {
    if (newLanguage !== language) {
      // Update i18n immediately
      setLanguage(newLanguage);
      // Then update backend
      updateLanguageMutation.mutate(newLanguage);
    }
    setIsOpen(false);
  };

  // Don't render until backend responds
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", padding: "0.5rem" }}>
        <CircularProgress size={20} sx={{ color: Colors.PURPLE_6750A4 }} />
      </Box>
    );
  }

  const currentLanguage = languages[language as keyof typeof languages] || languages.en;

  return (
    <DropdownMenu
      isOpen={isOpen}
      onToggle={() => setIsOpen(!isOpen)}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Avatar
            src={currentLanguage.flag}
            alt={currentLanguage.label}
            sx={{
              width: 28,
              height: 28,
              "& img": {
                objectFit: "cover",
              },
            }}
          />
          {updateLanguageMutation.isPending ? (
            <CircularProgress size={20} sx={{ color: Colors.PURPLE_6750A4 }} />
          ) : (
            <Typography
              variant="h6"
              sx={{
                color: Colors.PURPLE_6750A4,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {currentLanguage.label}
            </Typography>
          )}
        </Box>
      }
    >
      <MenuItem
        onClick={() => handleLanguageSelect("en")}
        selected={language === "en"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: `${Colors.BUTTON_PURPLE}15`,
            "&:hover": {
              backgroundColor: `${Colors.BUTTON_PURPLE}25`,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Avatar
            src={languages.en.flag}
            alt={languages.en.label}
            sx={{
              width: 28,
              height: 28,
              "& img": {
                objectFit: "cover",
              },
            }}
          />
          <Typography
            sx={{
              color: Colors.PURPLE_6750A4,
              fontWeight: language === "en" ? 700 : 400,
            }}
          >
            {languages.en.label}
          </Typography>
        </Box>
      </MenuItem>
      <MenuItem
        onClick={() => handleLanguageSelect("es")}
        selected={language === "es"}
        sx={{
          "&.Mui-selected": {
            backgroundColor: `${Colors.BUTTON_PURPLE}15`,
            "&:hover": {
              backgroundColor: `${Colors.BUTTON_PURPLE}25`,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Avatar
            src={languages.es.flag}
            alt={languages.es.label}
            sx={{
              width: 28,
              height: 28,
              "& img": {
                objectFit: "cover",
              },
            }}
          />
          <Typography
            sx={{
              color: Colors.PURPLE_6750A4,
              fontWeight: language === "es" ? 700 : 400,
            }}
          >
            {languages.es.label}
          </Typography>
        </Box>
      </MenuItem>
    </DropdownMenu>
  );
}
